/* MIRRORED FILE — an identical copy lives in the other project:
 *   game-client:      src/components/ui/use-settled.ts
 *   portal-frontend:  src/components/use-settled.ts
 * Keep byte-identical below this banner; apply any change to both copies in the
 * same commit. */

/**
 * use-settled.ts — whether a live region has been on the page long enough to
 * be heard: two frames, and a tenth of a second since the first frame that
 * drew it.
 *
 * A screen reader says a change to what a region holds. A region that arrives
 * with its words already in it has had none, nor has one whose words come in
 * the frame it arrives: the browser builds its accessibility tree once a
 * frame, so a screen reader would still meet the region holding them. Two
 * frames have the page drawn with the region empty, and a tenth of a second
 * is what react-aria's announcer waits, Safari being said to drop what comes
 * sooner. A region holds back its first words until this says so, then says
 * each change at once. Each one element with its clock reads it: each
 * project's StatusLine, a line said to a screen reader alone, and the game
 * client's LiveRegion, shown on the page as well. The game client's screens
 * walk watches every region as it arrives and holds it to this
 * (checkLiveRegions).
 *
 *   const settled = useSettled();
 *   <p role="status">{settled ? words : ''}</p>
 */

import { useEffect, useState } from 'react';

/**
 * How many frames, and how long since the first, a region new to the page
 * holds back its first words, at the least. The game client's screens walk
 * holds every live region it watches to the same (watchLiveRegions).
 */
export const SETTLE_FRAMES = 2;
export const SETTLE_MS = 100;

/**
 * Whether what calls it has been on the page two frames, and SETTLE_MS since
 * the first frame that drew it, read on the clock as that frame's callbacks
 * run. Not from the effect, which can run in the very task that inserted the
 * region, before anything watching the page could see it arrive; nor from the
 * frame's own timestamp, the vsync it was drawn for, which can come before a
 * region inserted in the same frame: either way a tenth of a second would be
 * a few milliseconds short of one counted from the region's arrival.
 */
export function useSettled(): boolean {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    let frames = 0;
    let drawn = 0;
    let frame = requestAnimationFrame(function count() {
      frames += 1;
      if (frames === 1) drawn = performance.now();
      if (frames >= SETTLE_FRAMES && performance.now() - drawn >= SETTLE_MS) setSettled(true);
      else frame = requestAnimationFrame(count);
    });
    return () => cancelAnimationFrame(frame);
  }, []);
  return settled;
}
